import { useCallback, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { triggerLoading } from 'store/forms/actions';
import { instance } from 'store/utils';

/**
 * @hook
 *
 * @summary Manages the state and logic for the `AIDrivenSearch` component using a reducer pattern.
 *
 * @description
 * The `useAIDrivenSearch` custom React hook encapsulates the complete logic behind
 * the AI Driven Search interface. It centralizes state management with `useReducer`
 * and provides memoized handlers for file upload and search submission.
 * Internal state includes file upload progress, search status.
 *
 * @pattern
 * This hook follows:
 * 1. The **Container–Presentational (Smart/Dumb)** component pattern, where logic lives in this hook or a parent container
 *    and UI rendering is delegated to a stateless component (`AIDrivenSearch`).
 * 2. A **Reducer-Driven Custom Hook** (also known as a *State Machine Hook* or *Business Logic Hook*), a pattern widely
 *    used in large React applications (e.g., Shopify Polaris, Vercel Dashboard) for scalable state management.
 *
 * @returns {object} Returns a structured object containing `state` and `handlers`.
 *
 * @returns {object} return.state - The current AI Driven Search state.
 * @returns {File|null} return.state.uploadedFile - The uploaded Excel file, if any.
 * @returns {string|null} return.state.uploadedFileId - The uploaded Excel file identifier from file upload response from backend.
 * @returns {boolean} return.state.isUploadingFile - Whether a file upload is currently in progress.
 * @returns {any|null} return.state.error - Whether an error occured in the upload.
 *
 * @returns {object} return.handlers - All available handler functions for manipulating state.
 * @returns {function(File|null):void} return.handlers.handleFileUpload - Handles uploading the Excel file.
 * @returns {function():Promise<void>} return.handlers.handleFileDeletion - Handles deleting the Excel file.
 * @returns {function():Promise<void>} return.handlers.handleSubmit - Simulates the AI-driven search process asynchronously.
 *
 * @example
 * const { state, handlers } = useAIDrivenSearch();
 *
 * handlers.handleFileUpload(file);
 * await handlers.handleSubmit();
 *
 * <AIDrivenSearch
 *   uploadedFile={state.uploadedFile}
 *   isUploadingFile={state.isUploadingFile}
 *   isSearching={isSearching}
 *   error={state.error}
 *   onFileUpload={handlers.handleFileUpload}
 *   onDeleteFile={handlers.handleFileDeletion}
 *   onSubmit={handleSubmit}
 * />
 */
export function useAIDrivenSearch() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchRedux = useDispatch();

  /**
   * @summary Handle file upload to backend
   */
  const handleFileUpload = useCallback(
    async (file, error) => {
      if (error) {
        dispatch({ type: 'UPLOAD_ERROR', payload: error });
        return;
      }

      if (!file) {
        dispatch({ type: 'UPLOAD_REMOVE' });
        return;
      }

      dispatch({ type: 'UPLOAD_START' });
      dispatchRedux(triggerLoading(true));
      const controller = new AbortController();
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await instance.post(
          '/proposal/investment-plans/excel',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
          }
        );
        const id = res?.data?.id;
        if (!res?.data?.id) {
          throw new Error('No id provided');
        }
        dispatch({
          type: 'UPLOAD_SUCCESS',
          payload: { file, id },
        });
      } catch (err) {
        dispatch({
          type: 'UPLOAD_ERROR',
          payload: err.message || 'Upload failed',
        });
      } finally {
        dispatchRedux(triggerLoading(false));
      }
    },
    [dispatchRedux]
  );

  /**
   * @summary Handle file deletion
   */
  const handleFileDeletion = useCallback(() => {
    dispatch({ type: 'UPLOAD_REMOVE' });
  }, []);

  const handlers = useMemo(
    () => ({
      handleFileUpload,
      handleFileDeletion,
    }),
    [handleFileUpload, handleFileDeletion]
  );

  return { state, handlers };
}

const initialState = {
  uploadedFile: null,
  uploadedFileId: null,
  errorUpload: null,
  isErrorUpload: false,
  isUploadingFile: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'UPLOAD_START':
      return {
        ...state,
        isUploadingFile: true,
        errorUpload: null,
        isErrorUpload: false,
      };
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        uploadedFile: action.payload.file,
        uploadedFileId: action.payload.id,
        isUploadingFile: false,
      };
    case 'UPLOAD_ERROR':
      return {
        ...state,
        errorUpload: action.payload,
        isErrorUpload: true,
        isUploadingFile: false,
      };
    case 'UPLOAD_REMOVE':
      return {
        ...state,
        uploadedFile: null,
        uploadedFileId: null,
        errorUpload: null,
        isErrorUpload: false,
        isUploadingFile: false,
      };
    default:
      return state;
  }
}
